import * as React from 'react';
import { debounce } from 'lodash';

import { assertions } from 'lib/assertions';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
// @ts-ignore
import Group from 'entities/Group';
// @ts-ignore
import GroupCreationLimitNoticeContainer from './GroupCreationLimitNotice';
// @ts-ignore
import GroupSchoolSelect from 'groups/components/GroupSchoolSelect';
// @ts-ignore
import PrependedTextInput from 'components/PrependedTextInput';
// @ts-ignore
import SettingsStore from 'stores/SettingsStore';
import errorLogger from 'utils/errorLogger';

// @ts-ignore
import { OrgOption } from 'groups/components/GroupSchoolSelect';
import { LoadOwnedGroupSummaries_me_classes } from 'groups/queries/__generated__/LoadOwnedGroupSummaries';

// @ts-ignore it cant actually technically be false since it gets created at the constructor
// unclear WHY this is outside of the class but leaving as is so I don't break some unforseen behaviour -Tor
let cfg: {
  GROUP_CODE_MAX_LENGTH: number;
  GROUP_CODE_MIN_LENGTH: number;
  GROUP_NAME_MAX_LENGTH: number;
  GROUP_NAME_MIN_LENGTH: number;
  GROUP_CODE_REGEX: RegExp;
} = {};

export type Props = {
  onFormValid: (name: string, code: string, orgId?: string, orgName?: string, orgUuid?: string) => void;
  onFormInvalid: () => void;
  onValidNameChange?: (name: string) => void;
  onCodeChange?: (code: string) => void;
  onSchoolChange?: (orgId?: string, orgName?: string, orgUuid?: string) => void;
  name: string;
  code: string;
  currentOrganization?: OrgOption;
  group?: Group;
  hasChildren: boolean;
  showOrg: boolean;
  autofocus: boolean;
};

type State = {
  nameFromField: string;
  codeFromField: string;
  classNameError: string | null;
  classCodeError: string | null;
  verifyingCodeUniqueness: boolean;
  selectedOrgUuid?: string;
  selectedOrgId?: string;
  selectedOrgName?: string;
  requestToJoinEnabled: boolean;
  ownedGroups?: (LoadOwnedGroupSummaries_me_classes | null)[];
};

export const GROUP_CODE_VALIDATION_REGEX = /^[a-zA-Z0-9\-]+$/;

class GroupForm extends React.Component<Props, State> {
  static defaultProps = {
    name: '',
    code: '',
    hasChildren: false,
    showOrg: true,
    autofocus: true,
  };

  constructor(props: Props) {
    super(props);

    const { group } = props;

    cfg = {
      GROUP_CODE_MAX_LENGTH: SettingsStore.getMaxClassCodeLength(),
      GROUP_CODE_MIN_LENGTH: SettingsStore.getMinClassCodeLength(),
      GROUP_NAME_MAX_LENGTH: SettingsStore.getMaxClassNameLength(),
      GROUP_NAME_MIN_LENGTH: SettingsStore.getMinClassNameLength(),
      GROUP_CODE_REGEX: GROUP_CODE_VALIDATION_REGEX,
    };

    this.state = {
      nameFromField: props.name,
      codeFromField: props.code,
      classCodeError: null,
      classNameError: null,
      verifyingCodeUniqueness: false,
      selectedOrgUuid: undefined,
      requestToJoinEnabled: group ? group.get('requestToJoinEnabled') : false,
      ownedGroups: undefined,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.name !== this.props.name) {
      this.setState({ nameFromField: this.props.name });
    }

    if (prevProps.code !== this.props.code) {
      this.setState({ codeFromField: this.props.code });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  groupSchoolSelect: GroupSchoolSelect;
  _isMounted: boolean = false;

  isMounted() {
    return this._isMounted;
  }

  handleNameChange = async (name: string) => {
    const { classCodeError, selectedOrgId, selectedOrgName, selectedOrgUuid } = this.state;

    // Clear previous errors
    this.setState({ classNameError: null, nameFromField: name });

    if (
      this.props.name &&
      name === this.props.name &&
      this.props.code === this.getCurrentCodeValue()
    ) {
      this.props.onFormInvalid();
      return;
    }

    if (!this.nameIsValid(name)) {
      return;
    }

    if (this.props.onValidNameChange) {
      this.props.onValidNameChange(name);
    }
    const code = this.getCurrentCodeValue();
    if (this.nameIsValid(name) && this.codeIsValid(code) && !classCodeError) {
      this.props.onFormValid(name, code, selectedOrgId, selectedOrgName, selectedOrgUuid);
    }
  };

  handleCodeChange = (code: string) => {
    // Notify parent that the form is invalid, so it can disable submit button
    this.props.onFormInvalid();

    // Clear previous errors
    this.setState({ classCodeError: null, verifyingCodeUniqueness: false, codeFromField: code });

    if (this.props.code && code.length === 0) {
      this.setState({ classCodeError: 'Class code is required.' });
      return;
    }

    // When editing an existing class, and the code is the same as existing code
    if (this.props.code && code === this.props.code) {
      // If the name has also changed to something valid
      if (
        this.props.name !== this.getCurrentNameValue() &&
        this.nameIsValid(this.getCurrentNameValue()) &&
        !this.state.classNameError
      ) {
        this.props.onFormValid(this.getCurrentNameValue(), code, this.state.selectedOrgId);
      } else {
        this.props.onFormInvalid();
      }
      return;
    }

    // If the code is empty, and the current name is valid, call the onFormValid handler
    // and skip other checks on the code (but only if this isn't an existing class)
    // This let's us generate a code for a new class created with a blank code.
    if (
      !code &&
      !this.props.code &&
      this.nameIsValid(this.getCurrentNameValue()) &&
      !this.state.classNameError
    ) {
      this.props.onFormValid(this.getCurrentNameValue(), code, this.state.selectedOrgId);
      return;
    }

    // Otherwise, check if the code is valid with client-side checks
    if (code && this.codeIsValid(code)) {
      // Turn on the class code input's loading indicator
      this.setState({ verifyingCodeUniqueness: true });

      // Then call the server to check if the code is unique
      this.validateGroupCodeUniqueness(code);
    }

    if (this.props.onCodeChange) {
      this.props.onCodeChange(code);
    }
  };

  handleSchoolChange = (orgId?: string, orgUuid?: string, orgName?: string) => {
    const code = this.getCurrentCodeValue();
    const name = this.getCurrentNameValue();

    this.setState({
      selectedOrgUuid: orgUuid,
      selectedOrgId: orgId,
      selectedOrgName: orgName,
    });

    if (this.nameIsValid(name) && this.codeIsValid(code) && !this.state.classCodeError) {
      this.props.onFormValid(name, code, orgId, orgName, orgUuid);
      if (this.props.onSchoolChange) {
        this.props.onSchoolChange(orgId, orgName, orgUuid);
      }
    }
  };

  getCurrentNameValue: () => string = () => {
    return this.state.nameFromField;
  };

  getCurrentCodeValue: () => string = () => {
    return this.state.codeFromField;
  };

  getOrganizationForSubmit: () => OrgOption = () => {
    if (this.groupSchoolSelect != null) {
      return this.groupSchoolSelect.getOrganizationForSubmit();
    } else {
      throw new Error(
        'Expected a <GroupSchoolSelect /> to be bound to this.groupSchoolSelect via React ref. But was null'
      );
    }
  };

  nameIsValid = (name: string) => {
    return (
      name && name.length >= cfg.GROUP_NAME_MIN_LENGTH && name.length <= cfg.GROUP_NAME_MAX_LENGTH
    );
  };

  codeIsValid = (code: string) => {
    let valid =
      code.length >= cfg.GROUP_CODE_MIN_LENGTH &&
      code.length <= cfg.GROUP_CODE_MAX_LENGTH &&
      cfg.GROUP_CODE_REGEX.test(code);

    if (!this.props.code) {
      valid = valid || code.length === 0;
    }
    return valid;
  };

  validateGroupCodeUniqueness = debounce((code: string) => {
    if (!this.isMounted()) {
      return;
    }

    this.props.onFormInvalid();
    Group.validateCodeUniqueness(code)
      .then((codes: any) => {
        // XXX move promise out of this view
        if (!this.isMounted()) {
          return;
        }

        // Disable the class code input's loading indicator
        this.setState({ verifyingCodeUniqueness: false });

        // If the code has changed since we dispatched this request,
        // then result is no longer valid, so don't overwrite the error message
        // This prevents these responses from overwriting length validation results.
        if (code !== this.getCurrentCodeValue()) {
          return;
        }

        // The server returns an array of suggested code when the code passed
        // to it was not unique (i.e. a duplicate)
        if (codes) {
          this.setState({
            classCodeError: `
                Oops! This code is unavailable. Here are some available codes:
                @${codes[0]}, @${codes[1]}, or @${codes[2]}.`,
          });
        } else if (
          this.nameIsValid(this.getCurrentNameValue()) &&
          !this.state.classNameError &&
          !this.state.classCodeError
        ) {
          // If no codes are returned, the code passed in was unique
          // So if the current name is also valid, call the onFormValid handler
          this.props.onFormValid(
            this.getCurrentNameValue(),
            this.getCurrentCodeValue(),
            this.state.selectedOrgId
          );
        }
      })
      .catch(errorLogger('GroupForm validateCodeUniqueness failed'));
  }, 300);

  render() {
    return (
      <div>
        <FormElement
          data-test="group-name-field"
          label="Class name"
          validate={e => {
            assertions.lengthGreaterThanOrEqualTo(
              e.target.value,
              cfg.GROUP_NAME_MIN_LENGTH,
              `The name must be at least ${cfg.GROUP_NAME_MIN_LENGTH} characters long.`
            );
            assertions.lengthLesserThanOrEqualTo(
              e.target.value,
              cfg.GROUP_NAME_MAX_LENGTH,
              `The name cannot be over ${cfg.GROUP_NAME_MAX_LENGTH} characters long.`
            );
            assertions.notEmpty(e.target.value, 'Class name is required.');
          }}
          onValidChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            this.handleNameChange(e.target.value)
          }
          onInvalidChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({ nameFromField: e.target.value });
            this.props.onFormInvalid();
          }}
          showErrorBeforeBlur
          manualError={this.state.classNameError}
          element={Input}
          elementProps={{
            required: true,
            autofocus: this.props.autofocus,
            name: 'class_name',
            value: this.state.nameFromField,
            className: 'GroupForm-groupName',
          }}
        />
        <FormElement
          data-test="group-code-field"
          label="Class code"
          validate={e => {
            assertions.lengthGreaterThanOrEqualTo(
              e.target.value,
              cfg.GROUP_CODE_MIN_LENGTH,
              `The code must be at least ${cfg.GROUP_CODE_MIN_LENGTH} characters long.`
            );
            assertions.lengthLesserThanOrEqualTo(
              e.target.value,
              cfg.GROUP_CODE_MAX_LENGTH,
              `Class code must be ${cfg.GROUP_CODE_MAX_LENGTH} characters or less.`
            );
            assertions.matchRegex(
              e.target.value,
              cfg.GROUP_CODE_REGEX,
              'The code can only contain characters a-Z and -'
            );
            assertions.notEmpty(e.target.value, 'Class code is required');
          }}
          onValidChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            this.handleCodeChange(e.target.value)
          }
          onInvalidChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({ codeFromField: e.target.value });
            this.props.onFormInvalid();
          }}
          showErrorBeforeBlur
          manualError={this.state.classCodeError}
          loading={this.state.verifyingCodeUniqueness}
          element={PrependedTextInput}
          elementProps={{
            required: true,
            name: 'name',
            value: this.state.codeFromField,
            className: 'GroupForm-groupCode',
            prepend: '@',
          }}
        />
        {this.props.showOrg && (
          <GroupSchoolSelect
            group={this.props.group}
            initialOrganization={this.props.currentOrganization}
            hideUnaffiliated={this.props.hasChildren}
            onSchoolChange={this.handleSchoolChange}
            input="select"
            ref={(select: GroupSchoolSelect) => (this.groupSchoolSelect = select)}
          />
        )}
        <GroupCreationLimitNoticeContainer
          selectedOrgUuid={this.state.selectedOrgUuid}
          group={this.props.group}
        />
      </div>
    );
  }
}

export default GroupForm;
